<template>
  <content-dialog
    :visible.sync="modalVisible"
    :fullscreen="$vuetify.breakpoint.smAndDown"
    max-width="500px"
    :title-padding="$vuetify.breakpoint.xsOnly ? 'pa-2' : 'pa-4'"
  >
    <!--<editor-fold desc="Dialog Title">-->
    <template slot="dialogTitle">
      Finish Service Task
    </template>
    <!--</editor-fold>-->

    <!--<editor-fold desc="Dialog Content">-->
    <v-card-text :class="{'pa-4': $vuetify.breakpoint.smAndUp }">
      <validation-observer ref="validationObserver">
        <div v-if="serviceTask">
          <div class="font-weight-bold mb-2">
            Previous Work
          </div>

          <div
            v-if="workLogs.length > 0"
            class="grey lighten-3 pa-1"
          >
            <v-row
              v-for="workLog in workLogs"
              :key="'workLog-'+workLog.id"
              class="list--bordered pa-1 caption"
              no-gutters
            >
              <v-col
                cols="auto"
                class="text-no-wrap"
              >
                <strong>{{ workLog.done_at | moment('D MMM YYYY') }}</strong>
              </v-col>
              <v-col class="px-3">
                {{ workLog.done_by.name }}
              </v-col>
              <v-col cols="auto">
                {{ workLog.hours_spent }}h
              </v-col>
            </v-row>
          </div>

          <div
            v-else
            class="grey lighten-3 text-center caption grey--text pa-4 radius-10"
          >
            No work logged. Add work log and finish.
          </div>

          <v-divider class="my-3" />
        </div>

        <v-switch
          v-model="addWorkLog"
          :disabled="workLogs.length === 0"
          label="Add Work Log Today"
        />

        <div v-if="addWorkLog">
          <validation-provider
            v-slot="{ errors }"
            rules="required|min_value:0.1"
            name="hours spent"
          >
            <v-text-field
              v-model="hoursSpentDirty"
              v-validate="'required|min_value:0.1'"
              :error-messages="errors"
              type="number"
              step="0.5"
              min="0"
              label="Hours Spent"
              placeholder="Enter hours spent..."
              outlined
            />
          </validation-provider>

          <validation-provider
            v-slot="{ errors }"
            rules="required"
            name="finished by"
          >
            <assignee-modal-select
              v-model="assignedToDirty"
              v-validate="'required'"
              :dialog-visible.sync="assigneeModalSelectVisible"
              :error-messages="errors"
              multiple
              outlined
              clearable
              small-chips
              deletable-chips
              label="Work Done By"
              @submit="assigneeModalSelectVisible = false"
            />
          </validation-provider>
        </div>
      </validation-observer>
    </v-card-text>
    <!--</editor-fold>-->

    <!--<editor-fold desc="Dialog Actions">-->
    <template slot="dialogActions">
      <v-spacer />
      <v-btn
        text
        color="grey"
        @click="modalVisible = false"
      >
        Cancel
      </v-btn>
      <v-btn
        color="primary"
        :loading="submitting"
        @click="submit()"
      >
        <v-icon
          small
          left
        >
          check
        </v-icon>
        {{ addWorkLog ? 'Add Work Log & Finish' : 'Mark Task As Finished' }}
      </v-btn>
    </template>
    <!--</editor-fold>-->
  </content-dialog>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep'
import ServiceTask from '@/models/ServiceTask'
import ContentDialog from '@/components/helpers/Dialog'
import AssigneeModalSelect from '@/components/service/helpers/AssigneeModalSelect'
import validationScrollMixin from '@/mixins/validationScrollMixin'

export default {
    name: 'ServiceTaskFinishDialog',
    components: {
      AssigneeModalSelect,
      ContentDialog
    },
    mixins: [
      validationScrollMixin
    ],
    props: {
      visible: Boolean,
      serviceTask: {
        type: Object,
        default: () => undefined
      },
      serviceTaskIds: {
        type: Array,
        default: () => []
      }
    },
    data() {
      return {
        addWorkLog: true,
        assigneeModalSelectVisible: false,
        hoursSpentDirty: null,
        assignedToDirty: [],
        workLogs: [],
        submitting: false
      }
    },
    computed: {
      modalVisible: {
        get: function () {
          return this.visible
        },
        set: function (value) {
          this.$emit('update:visible', value)
        }
      },
      currentEmployee() {
        return this.$store.state.auth.user.staff
      }
    },
    watch: {
      visible(value) {
        if (!value) return

        this.fetchWorkLogs()
      }
    },
    mounted() {
      this.fetchWorkLogs()

      this.pushCurrentEmployee()
    },
    methods: {
      async submit() {
        let validate = await this.$refs.validationObserver.validate()

        if (!validate) {
          this.showAlertAndScrollToFirstError()
          return
        }

        this.submitting = true

        try {
          let data = {
            add_work_log: this.addWorkLog
          }

          if (this.addWorkLog) {
            data.hours_spent = this.hoursSpentDirty
            data.assigned_to = this.assignedToDirty.map(assignee => {
              return {
                type: assignee.type,
                id: assignee.id
              }
            })
          }

          const serviceTask = await ServiceTask.api.finishServiceTask(this.serviceTask.id, data)

          this.$emit('serviceTask:updated', serviceTask)

          this.$store.dispatch('alert', {
            text: 'Work successfully logged',
            color: 'success'
          })

          this.modalVisible = false

          this.assignedToDirty = []
          this.pushCurrentEmployee()
          this.hoursSpentDirty = null
        } catch (e) {
          this.$store.dispatch('alert', {
            'text': 'Something went wrong',
            'color': 'error'
          })

          throw e
        } finally {
          this.submitting = false
        }
      },
      pushCurrentEmployee() {
        let employee = cloneDeep(this.currentEmployee)
        employee.type = ServiceTask.ASSIGNEE_TYPE_STAFF

        this.assignedToDirty.push(employee)
      },
      async fetchWorkLogs() {
        if (!this.serviceTask) {
          this.workLogs = []
          this.addWorkLog = true
          return
        }

        try {
          const workLogs = await ServiceTask.api.fetchWorkLogs(this.serviceTask.id)

          this.workLogs = workLogs

          if (workLogs.length === 0) {
            this.addWorkLog = true
          }
        } catch (e) {
          throw e
        }
      }
    }
  }
</script>
